import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useNotifications } from '../../contexts/NotificationsContextType';
import { useProfile } from '../../contexts/ProfileContext';
import { useAcheteurProfile } from '../../contexts/AcheteurProfileContext';
import { isClientRole, isPrestataireRole } from '../../utils/roleUtils';

// Icons as React components
import { ReactComponent as AnnuaireIcon } from "../../assets/icons/client-list-blanc.svg";
import { ReactComponent as AccueilIcon } from "../../assets/icons/accueil.svg";
import { ReactComponent as PublicationsIcon } from "../../assets/icons/order-blanc.svg";
import { ReactComponent as CommandesIcon } from "../../assets/icons/commandes.svg";
import { ReactComponent as CatalogueIcon } from "../../assets/icons/catalogue-blanc.svg";
import { ReactComponent as ProfilIcon } from "../../assets/icons/profil-blanc.svg";
import { ReactComponent as DocumentsIcon } from "../../assets/icons/document-blanc.svg";
import { ReactComponent as SupportIcon } from "../../assets/icons/support-blanc.svg";
import { ReactComponent as LogoutIcon } from "../../assets/icons/logout-blanc.svg";

import "./SideBar.css";

interface SidebarProps {
  unreadCommandes?: number;
  isCollapsed: boolean;
  onCollapse: (collapsed: boolean) => void;
}

interface LinkItem {
  path: string;
  label: string;
  icon: string;
  notifications?: number;
  show: boolean;
  disabled?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ isCollapsed, onCollapse }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { unreadCommandes = 0, unreadOperations = 0, unreadCandidatures = 0, refresh: refreshNotifications } = useNotifications() || {};

  const [isUserAcheteur, setIsUserAcheteur] = useState<boolean | null>(null);
  const [sidebarLinks, setSidebarLinks] = useState<LinkItem[]>([]);

  // FRONT-10: État pour la complétion du profil (Prestataires Libres uniquement)
  // On écoute les updates pour rafraîchir la pastille en direct
  const { completion, refreshCompletion } = useProfile?.() || { completion: null, refreshCompletion: undefined };
  const { completion: acheteurCompletion, refreshCompletion: refreshAcheteurCompletion } = useAcheteurProfile?.() || { completion: null, refreshCompletion: undefined };

  useEffect(() => {
    if (!refreshCompletion) return;
    const handler = () => { refreshCompletion(); };
    window.addEventListener('profile-updated', handler);
    return () => window.removeEventListener('profile-updated', handler);
  }, [refreshCompletion]);

  useEffect(() => {
    if (!refreshAcheteurCompletion) return;
    const handler = () => { refreshAcheteurCompletion(); };
    window.addEventListener('acheteur-profile-updated', handler);
    return () => window.removeEventListener('acheteur-profile-updated', handler);
  }, [refreshAcheteurCompletion]);

  // Les pastilles (completion + compteurs notifications) doivent rester a jour SANS devoir passer
  // par l'onglet Profil. La sidebar est montee en permanence : on rafraichit a CHAQUE navigation
  // (et au login), selon le role. Le changement de route est le declencheur.
  useEffect(() => {
    if (!user || isUserAcheteur === null) return;
    refreshNotifications?.();
    if (isUserAcheteur) {
      refreshAcheteurCompletion?.();
    } else if (user.role && isPrestataireRole(user.role)) {
      refreshCompletion?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, user, isUserAcheteur]);

  // Déterminer le rôle et construire les liens dynamiquement
  useEffect(() => {
    const userDataString = localStorage.getItem('user');
    let isAcheteur = false;
    if (userDataString) {
      try {
        const userData = JSON.parse(userDataString);
        isAcheteur = ["Acheteur", "Acheteur signataire", "Acheteur signataire admin"].includes(userData.role);
      } catch (error) {
        console.error("Erreur lors du parsing des données utilisateur:", error);
      }
    }

    setIsUserAcheteur(isAcheteur);

    const links: LinkItem[] = [
      {
        path: '/',
        label: 'Accueil',
        icon: 'accueil-icon',
        show: !user
      },
      {
        path: '/publications',
        label: 'Publications',
        icon: 'publications-icon',
        notifications: unreadCandidatures,
        show: true,
      },
      {
        path: '/commandes',
        label: 'Commandes',
        icon: 'commandes-icon',
        notifications: unreadCommandes,
        show: !!user && !isAcheteur
      },

      {
        path: '/operations',
        label: 'Commandes',
        icon: 'operations-icon',
        notifications: unreadOperations,
        show: !!user && isAcheteur
      },
      // {
      //   path: '/commandes-archive',
      //   label: 'Commandes importées',
      //   icon: 'commandes-icon',
      //   // notifications: unreadCommandes,
      //   show: !!user
      // },
      {
        // Chaque profil voit l'autre partie en priorite (acheteur -> prestataires, presta -> acheteurs).
        path: isAcheteur ? '/annuaire?tab=prestataires' : '/annuaire?tab=acheteurs',
        label: 'Annuaire',
        icon: 'annuaire-icon',
        show: true,
      },
      {
        path: '/profil',
        label: 'Profil',
        icon: 'profil-icon',
        show: !!user
      },
      {
        path: '/assistance',
        label: 'Assistance',
        icon: 'support-icon',
        show: true
      },
      {
        path: '/mentions',
        label: 'Mentions',
        icon: 'catalogue-icon',
        show: true
      }
    ];

    setSidebarLinks(links.filter(link => link.show));
  }, [user, unreadCommandes, unreadOperations, unreadCandidatures]);

  // FRONT-10: Charger la complétion du profil pour les Prestataires Libres
  // CORE-19: Ne pas appeler l'API pour les acheteurs
  // FIX: Reset immédiat au changement de compte pour éviter la pastille stale
  // Suppression du useEffect de récupération manuelle, on passe par le contexte

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <aside className={`sidebar ${isCollapsed ? "collapsed" : ""}`}>
      <div className="first-section">
        {/* Plus de logo dans la sidebar : il vit dans la barre d'en-tete (UserInfo). Simple espaceur haut. */}
        <div className="sidebar-header" aria-hidden="true" />
        <ul className="sidebar-menu">
          {sidebarLinks.map((link) => {
            let IconComponent;
            switch (link.icon) {
              case 'commandes-icon':
              case 'operations-icon':
                IconComponent = CommandesIcon;
                break;
              case 'annuaire-icon':
                IconComponent = AnnuaireIcon;
                break;
              case 'publications-icon':
                IconComponent = PublicationsIcon;
                break;
              case 'accueil-icon':
                IconComponent = AccueilIcon;
                break;
              case 'profil-icon':
                IconComponent = ProfilIcon;
                break;
              case 'support-icon':
                IconComponent = SupportIcon;
                break;
              case 'catalogue-icon':
                IconComponent = CatalogueIcon;
                break;
              default:
                IconComponent = DocumentsIcon;
            }

            return (
              <li key={link.label}>
                <NavLink
                  to={link.disabled ? "#" : link.path} // si désactivé, pas de navigation
                  className={({ isActive }) => `sidebar-link ${isActive && !link.disabled ? 'active' : ''} ${link.disabled ? 'disabled' : ''}`}
                  onClick={(e) => link.disabled && e.preventDefault()} // bloquer le clic
                >
                  <IconComponent className="icon" />
                  <span className="sidebar-label">{link.label}</span>
                  {(link.path === '/commandes' || link.path === '/operations' || link.path === '/publications') && (
                    typeof link.notifications === "number" && link.notifications > 0 && (
                      <span className="badge">
                        {link.notifications > 999
                          ? `+${Math.floor(link.notifications / 1000)}k`
                          : link.notifications > 99
                            ? "+99"
                            : link.notifications}
                      </span>
                    )
                  )}
                  {/* FRONT-10: Badge profil incomplet pour Prestataires Libres */}
                  {link.path === '/profil' && completion && completion.percent < 100 && (
                    <span className="badge badge-warning">{completion.percent}%</span>
                  )}
                  {/* Badge profil incomplet pour Acheteurs */}
                  {link.path === '/profil' && acheteurCompletion && acheteurCompletion.percent < 100 && (
                    <span className="badge badge-warning">{acheteurCompletion.percent}%</span>
                  )}
                </NavLink>
              </li>
            );
          })}
        </ul>
        <ul className="sidebar-footer">
          <li>
            {user ? (
              <button onClick={handleLogout} className="sidebar-link logout-btn">
                <LogoutIcon className="icon" />
                <span className="sidebar-label">Déconnexion</span>
              </button>
            ) : (
              <NavLink to='/login' className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
              >
                <ProfilIcon className="icon" />
                <span className="sidebar-label">Connexion</span>
              </NavLink>
            )}
          </li>
        </ul>
      </div>
    </aside>
  );
};

export default Sidebar;